import * as Phaser from 'phaser';
import type { SpaceStationState } from '@rttv/shared';

interface PointLight {
  rect:   Phaser.GameObjects.Rectangle;
  halo:   Phaser.GameObjects.Rectangle;
  color:  number;
  rate:   number;   // radians per ms
  phase:  number;
  strobe: boolean;  // true = brief flash, false = smooth pulse
}

export class StationSprite {
  private body:     Phaser.GameObjects.Image;
  private pad0:     Phaser.GameObjects.Image;
  private pad1:     Phaser.GameObjects.Image;
  private glowGfx:  Phaser.GameObjects.Graphics;
  private lights:   PointLight[] = [];
  private state:    SpaceStationState;
  private glowTime  = 0;

  constructor(scene: Phaser.Scene, state: SpaceStationState) {
    this.state = state;

    this.body = scene.add.image(state.x, state.y, 'ritv_repair_station')
      .setRotation(state.rotation)
      .setDisplaySize(245, 245)
      .setDepth(5);

    this.pad0 = scene.add.image(state.pad0X, state.pad0Y, 'ritv_repair_pad')
      .setDisplaySize(81, 81)
      .setDepth(6);

    this.pad1 = scene.add.image(state.pad1X, state.pad1Y, 'ritv_repair_pad')
      .setDisplaySize(81, 81)
      .setDepth(6);

    this.glowGfx = scene.add.graphics().setDepth(4);

    // Blinking navigation / docking lights around the station body.
    // Station points up; world half-height ≈ 115px, half-width ≈ 69px.
    const LIGHT_DEFS: Array<{
      ox: number; oy: number; color: number; size: number;
      rate: number; phase: number; strobe: boolean;
    }> = [
      // Top docking beacon — cyan pulse
      { ox:   0, oy: -108, color: 0x44ccff, size: 6, rate: 0.0025, phase: 0,           strobe: false },
      // Bottom docking beacon — cyan pulse, half-cycle offset
      { ox:   0, oy:  108, color: 0x44ccff, size: 6, rate: 0.0025, phase: Math.PI,     strobe: false },
      // Left wing — red strobe (aviation port-side)
      { ox: -60, oy:  -10, color: 0xff2200, size: 5, rate: 0.0055, phase: 0,           strobe: true  },
      // Right wing — green strobe (aviation starboard-side)
      { ox:  60, oy:  -10, color: 0x00ff66, size: 5, rate: 0.0055, phase: 2.1,         strobe: true  },
      // Upper-body amber ident light
      { ox:   0, oy:  -50, color: 0xffaa00, size: 4, rate: 0.0015, phase: 1.2,         strobe: false },
      // Lower-body amber ident light
      { ox:   0, oy:   50, color: 0xffaa00, size: 4, rate: 0.0015, phase: 1.2 + Math.PI, strobe: false },
    ];

    for (const d of LIGHT_DEFS) {
      const halo = scene.add.rectangle(state.x + d.ox, state.y + d.oy, d.size * 3, d.size * 3, d.color, 1)
        .setOrigin(0.5, 0.5).setDepth(7) as Phaser.GameObjects.Rectangle;
      const rect = scene.add.rectangle(state.x + d.ox, state.y + d.oy, d.size, d.size, d.color, 1)
        .setOrigin(0.5, 0.5).setDepth(8) as Phaser.GameObjects.Rectangle;
      this.lights.push({ rect, halo, color: d.color, rate: d.rate, phase: d.phase, strobe: d.strobe });
    }
  }

  update(delta: number) {
    this.glowTime += delta;
    const pulse = 0.5 + 0.5 * Math.sin(this.glowTime * 0.0025);

    this.glowGfx.clear();
    this.drawPadGlow(this.state.pad0X, this.state.pad0Y, this.state.pad0Healing, pulse);
    this.drawPadGlow(this.state.pad1X, this.state.pad1Y, this.state.pad1Healing, pulse);

    for (const light of this.lights) {
      const raw   = (Math.sin(this.glowTime * light.rate + light.phase) + 1) / 2; // 0–1
      const alpha = light.strobe ? Math.pow(raw, 5) : 0.15 + raw * 0.85;
      (light.rect as any).setFillStyle(light.color, alpha);
      (light.halo as any).setFillStyle(light.color, alpha * 0.25);
    }
  }

  private drawPadGlow(x: number, y: number, healing: boolean, pulse: number) {
    const base = healing ? 0.22 + pulse * 0.25 : 0.08 + pulse * 0.06;
    // Outer soft halo — slightly larger than the 84px pad sprite
    this.glowGfx.fillStyle(0xff2200, base * 0.30);
    this.glowGfx.fillCircle(x, y, 56);
    // Inner glow core
    this.glowGfx.fillStyle(0xff3300, base * 0.70);
    this.glowGfx.fillCircle(x, y, 32);
  }

  destroy() {
    this.body.destroy();
    this.pad0.destroy();
    this.pad1.destroy();
    this.glowGfx.destroy();
    for (const light of this.lights) {
      light.rect.destroy();
      light.halo.destroy();
    }
  }
}
